import { useEffect, useRef } from 'react';
import { ChevronLeft, Mail, MessageCircle, Phone } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';

import { cn } from '../../../lib/cn';
import { mailtoHref, telHref, whatsappHref } from '../../../lib/quickActions';
import { ScoreBar } from '../ScoreBar';
import type { LeadDisplayStatus, LeadRow, LeadScoreClass, LeadSourceRow } from './leads.schema';
import styles from './leads.module.css';

const STATUS_LABELS: Record<LeadDisplayStatus, string> = {
  new: 'חדש',
  contacted: 'יצרנו קשר',
  qualified: 'הוסמך',
  disqualified: 'לא רלוונטי',
  converted: 'הומר לדיל',
};

function initialsFor(name: string): string {
  return name
    .trim()
    .slice(0, 2)
    .toUpperCase();
}

function scoreClass(value: string | null): LeadScoreClass {
  if (value === 'hot' || value === 'warm' || value === 'cold') return value;
  return null;
}

function statusLabel(status: string): string {
  if (
    status === 'new' ||
    status === 'contacted' ||
    status === 'qualified' ||
    status === 'disqualified' ||
    status === 'converted'
  ) {
    return STATUS_LABELS[status];
  }
  return status;
}

function relativeTime(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { locale: he, addSuffix: true });
  } catch {
    return iso;
  }
}

interface RowQuickActionsProps {
  phone: string | null;
  email: string | null;
}

function RowQuickActions({ phone, email }: RowQuickActionsProps) {
  const wa = whatsappHref(phone);
  const tel = telHref(phone);
  const mailto = mailtoHref(email);
  if (!wa && !tel && !mailto) return null;
  return (
    <span
      className={styles.rowQuickActions}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer noopener"
          className={styles.rowQuickActionBtn}
          aria-label="פתח WhatsApp"
          title="WhatsApp"
        >
          <MessageCircle size={12} aria-hidden="true" />
        </a>
      ) : null}
      {mailto ? (
        <a
          href={mailto}
          className={styles.rowQuickActionBtn}
          aria-label="שלח אימייל"
          title="אימייל"
        >
          <Mail size={12} aria-hidden="true" />
        </a>
      ) : null}
      {tel ? (
        <a
          href={tel}
          className={styles.rowQuickActionBtn}
          aria-label="התקשר"
          title="חיוג"
        >
          <Phone size={12} aria-hidden="true" />
        </a>
      ) : null}
    </span>
  );
}

interface LeadListProps {
  leads: LeadRow[];
  sources: LeadSourceRow[];
  selectedIds: Set<string>;
  onToggleLead: (id: string) => void;
  onToggleAllVisible: () => void;
  onSelect: (lead: LeadRow) => void;
}

export function LeadList({
  leads,
  sources,
  selectedIds,
  onToggleLead,
  onToggleAllVisible,
  onSelect,
}: LeadListProps) {
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const allVisibleSelected =
    leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));
  const someVisibleSelected = leads.some((lead) => selectedIds.has(lead.id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [allVisibleSelected, someVisibleSelected]);

  const sourceById = sources.reduce<Record<string, string>>((acc, source) => {
    acc[source.id] = source.name;
    return acc;
  }, {});

  return (
    <div className={styles.table} role="table" aria-label="לידים">
      <div className={cn(styles.rowGrid, styles.head)} role="row">
        <div role="columnheader">
          <label
            className={styles.checkboxLabel}
            title={allVisibleSelected ? 'בטל בחירה' : 'בחר הכל'}
          >
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allVisibleSelected}
              aria-label={allVisibleSelected ? 'בטל בחירה' : 'בחר הכל'}
              onChange={onToggleAllVisible}
            />
          </label>
        </div>
        <div role="columnheader" />
        <div role="columnheader">ליד</div>
        <div role="columnheader">מקור</div>
        <div role="columnheader">ציון</div>
        <div role="columnheader">סטטוס</div>
        <div role="columnheader">נקלט</div>
        <div role="columnheader" />
      </div>

      {leads.map((lead) => {
        const klass = scoreClass(lead.score_class);
        const sourceName = lead.source_id ? (sourceById[lead.source_id] ?? '—') : '—';

        return (
          <div
            key={lead.id}
            className={cn(styles.rowGrid, selectedIds.has(lead.id) && styles.rowSelected)}
            role="row"
            tabIndex={0}
            onClick={() => {
              onSelect(lead);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(lead);
              }
            }}
          >
            <label
              className={styles.checkboxLabel}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <input
                type="checkbox"
                checked={selectedIds.has(lead.id)}
                aria-label={`בחר את ${lead.full_name}`}
                onChange={() => {
                  onToggleLead(lead.id);
                }}
              />
            </label>
            <span
              className={cn(styles.avatar, klass ? styles[klass] : styles.cold)}
              aria-hidden="true"
            >
              {initialsFor(lead.full_name)}
            </span>
            <span className={styles.leadCell}>
              <strong>{lead.full_name}</strong>
              {lead.company_name ? <small>{lead.company_name}</small> : null}
            </span>
            <span>{sourceName}</span>
            <span className={styles.scoreCell}>
              <ScoreBar score={lead.score} klass={klass} />
            </span>
            <span className={styles.statusBadge}>{statusLabel(lead.status)}</span>
            <span className={styles.timeCell}>
              <span className="num">{relativeTime(lead.received_at)}</span>
            </span>
            <span className={styles.rowEnd}>
              <RowQuickActions phone={lead.phone} email={lead.email} />
              <ChevronLeft size={16} className={styles.chevron} aria-hidden="true" />
            </span>
          </div>
        );
      })}
    </div>
  );
}
